import {
  createContext,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import IApiResponse from 'models/IApiResponse';
import { useMenuItemsQuery, useMenusQuery } from '../services/menu/menu.queries';
import {
  useCartItemsQuery,
  useSaveCartMutation,
  useUpdatePendingCartMutation,
} from '../services/cart/cart.queries';
import { useMenuWithCartItems } from '../hooks/useMenuWithCartItems';
import { Menu, MenuCartItem, MenuList } from '../models/menu.types';
import { Cart } from '../models/cart.types';
import { useAuth } from './AuthContext';
import {
  initialDropdownFilter,
  initialFilter,
  SelectedDropdownFilters,
  SelectedFilter,
  TopLevelFilters,
} from '../constants/menu';
import { SignupStep } from '../constants/signup';
import { useReactivateSubscriptionMutation } from '../services/user/user.queries';
import { useFavoriteProduct, useUnFavoriteProduct } from '../services/product/product.queries';

type Props = {
  signupStep?: SignupStep
  children: ReactNode
}

type ContextValue = {
  upcomingMenus: MenuList | undefined;
  menuWithCartItems: MenuCartItem[];
  cart: Cart | undefined,
  menu: Menu | undefined,
  isMenuLoading: boolean;
  isCartLoading: boolean;
  menuError: unknown;
  cartError: unknown;
  saveCart: () => void;
  addToCart: (menuItem: MenuCartItem, isPromoCartItem?: boolean, suppressDrawer?: boolean) => void;
  removeFromCart: (cartItem: MenuCartItem) => void;
  updateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  discardCartChanges: () => void;
  onSelectMenu: (selectedMenuId: number, selectedCartId: number) => void;
  isCartDrawerOpen: boolean;
  setIsCartDrawerOpen: (value: boolean) => void;
  isCartChanged: boolean;
  setIsCartChanged: (value: boolean) => void;
  displayFreeShippingUnlockedBanner: boolean;
  setDisplayFreeShippingUnlockedBanner: (value: boolean) => void;
  displayFreeShippingUnlockedNotification: boolean;
  setDisplayFreeShippingUnlockedNotification: (value: boolean) => void;
  currentSignupStep: SignupStep | undefined;
  setCurrentSignupStep: (value: SignupStep | undefined) => void;
  displayChangeCartAddress: boolean;
  setDisplayChangeCartAddress: (value: boolean) => void;
  cartShippingAddressId: number;
  setCartShippingAddressId: (value: number) => void;
  displayCartUpdateAwareness: boolean;
  recalculateCart: (cart: Cart) => Promise<Cart | undefined>;
  selectedFilters: SelectedFilter;
  setSelectedFilters: (selectedFilters: SelectedFilter, userInitiated: boolean) => void;
  selectedDropdownFilters: SelectedDropdownFilters;
  setSelectedDropdownFilters: (selectedDropdownFilters: SelectedDropdownFilters,
    userInitiated: boolean) => void;
  filteredResultCount: number;
  setFilteredResultCount: (filteredResultCount: number) => void;
  canIncreaseQuantity: (menuItem: MenuCartItem) => boolean;
  reactivateSubscription: () => Promise<void>;
  addFavorite(skuId: number): Promise<void>;
  removeFavorite(skuId: number): Promise<void>;
  toggleSkip: () => Promise<void>;
  numFavoriteItems: number;
  setNumFavoriteItems: (value: number) => void;
};

const MenuWithCartContext = createContext<ContextValue | null>(null);
const REMOVED_CART_ITEM_TIMEOUT = 2000;

export function MenuWithCartProvider({
  signupStep,
  children,
}: Props): ReactElement {
  const { isLoggedIn } = useAuth();
  const [activeMenuId, setActiveMenuId] = useState(0);
  const [activeCartId, setActiveCartId] = useState(0);
  const [cart, setCart] = useState<Cart>();
  const [menu, setMenu] = useState<Menu>();
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isCartChanged, setIsCartChanged] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [displayFreeShippingUnlockedBanner, setDisplayFreeShippingUnlockedBanner] = useState(false);
  const [displayFreeShippingUnlockedNotification,
    setDisplayFreeShippingUnlockedNotification] = useState(false);
  const [currentSignupStep, setCurrentSignupStep] = useState<SignupStep | undefined>(signupStep);
  const [displayChangeCartAddress, setDisplayChangeCartAddress] = useState(false);
  const [cartShippingAddressId, setCartShippingAddressId] = useState<number>(0);
  const [displayCartUpdateAwareness, setDisplayCartUpdateAwareness] = useState(false);
  const [selectedFilters, setSelectedFiltersState] = useState<SelectedFilter>(() => {
    const params = new URLSearchParams(window.location.search);
    const category = params.get('category') as TopLevelFilters | null;
    const tags = params.get('filter_group')
      ?.split(',')
      .filter(Boolean) ?? [];
    const validCategory = (category && Object.values(TopLevelFilters)
      .includes(category))
      ? category as TopLevelFilters
      : null;
    if (validCategory || tags.length) {
      return { [validCategory ?? TopLevelFilters.ViewAll]: tags.length ? tags : ['All'] };
    }
    return initialFilter;
  });
  const [selectedDropdownFilters,
    setSelectedDropdownFiltersState] = useState<SelectedDropdownFilters>(
      () => {
        const params = new URLSearchParams(window.location.search);
        const productType = params.get('product_type')
          ?.split(',')
          .filter(Boolean) ?? [];
        const cookTime = params.get('cook_time')
          ?.split(',')
          .filter(Boolean) ?? [];
        if (productType.length || cookTime.length) {
          return {
            productType,
            cookTime,
          };
        }
        return initialDropdownFilter;
      },
    );

  const setSelectedFilters = (filters: SelectedFilter, userInitiated: boolean) => {
    if (userInitiated) sessionStorage.setItem('isUserInitiatedFilter', 'true');
    setSelectedFiltersState(filters);
  };
  const setSelectedDropdownFilters = (filters: SelectedDropdownFilters, userInitiated: boolean) => {
    if (userInitiated) sessionStorage.setItem('isUserInitiatedFilter', 'true');
    setSelectedDropdownFiltersState(filters);
  };
  const prevCartId = useRef<number | undefined>(undefined);
  const [filteredResultCount, setFilteredResultCount] = useState(0);
  const [numFavoriteItems, setNumFavoriteItems] = useState(3);

  const { data: upcomingMenus } = useMenusQuery();
  const {
    data: persistedMenu,
    isLoading: isPersistedMenuLoading,
    error: persistedMenuError,
  } = useMenuItemsQuery(activeMenuId);

  const {
    data: persistedCart,
    isLoading: isPersistedCartLoading,
    error: persistedCartError,
  } = useCartItemsQuery(activeCartId);

  useEffect(() => {
    // set the active menu & cart ids to the current week if we are initializing for the first time
    if (!activeMenuId && !activeCartId
      && upcomingMenus?.currentMenuId
      && upcomingMenus?.currentCartId) {
      setActiveMenuId(upcomingMenus.currentMenuId);
      setActiveCartId(upcomingMenus.currentCartId);
    }
  }, [activeMenuId, activeCartId, upcomingMenus]);

  const favoriteProduct = useFavoriteProduct(activeCartId, activeMenuId);
  const unFavoriteProduct = useUnFavoriteProduct(activeCartId, activeMenuId);

  useEffect(() => {
    if (cart?.shippingAddressId) {
      setCartShippingAddressId(cart?.shippingAddressId);
    }
  }, [cart?.shippingAddressId]);

  useEffect(() => {
    // set the cart if we are initializing for the first time,
    // if we are changing to a different cart week,
    // or if we are skipping/unskipping a cart
    if ((persistedCart && !cart)
      || (persistedCart?.id !== cart?.id)
      || (persistedCart?.isSkipped !== cart?.isSkipped)
      || (persistedCart?.plan.name !== cart?.plan.name)
      || (persistedCart?.plan.isDiversified !== cart?.plan.isDiversified)
      || (persistedCart?.plan.extras.breakfast !== cart?.plan.extras.breakfast)
      || (persistedCart?.plan.extras.lunch !== cart?.plan.extras.lunch)
      || (persistedCart?.plan.extras.dinner !== cart?.plan.extras.dinner)
    ) {
      setCart(persistedCart);
    }
  }, [cart, persistedCart]);

  useEffect(() => {
    if (activeMenuId && upcomingMenus && isLoggedIn) {
      const activeMenu = upcomingMenus.menus.find((m) => m.id === activeMenuId);
      setMenu(activeMenu ? { ...activeMenu, prospectBanner: persistedMenu?.prospectBanner } : activeMenu);
    } else if (persistedMenu) {
      setMenu(persistedMenu);
    }
  }, [activeMenuId, upcomingMenus, persistedMenu]);

  useEffect(() => {
    // if the user is mid-signup flow, set the drawer open so they can resume signup
    if (currentSignupStep && currentSignupStep !== SignupStep.CREATE_ACCOUNT) {
      setIsCartDrawerOpen(true);
    }
  }, [currentSignupStep]);

  useEffect(() => {
    // Reset filters only when the user changes between menu weeks (not on initial cart load)
    if (prevCartId.current !== undefined && prevCartId.current !== cart?.id) {
      setSelectedFilters(initialFilter, false);
      setSelectedDropdownFilters(initialDropdownFilter, false);
    }
    prevCartId.current = cart?.id;
  }, [cart?.id]);

  const menuWithCartItems = useMenuWithCartItems(persistedMenu, cart);
  const updatePendingCartMutation = useUpdatePendingCartMutation();
  const saveCartMutation = useSaveCartMutation();
  const timeoutMapRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  const handleCartUpdated = () => {
    setDisplayCartUpdateAwareness(true);
    setTimeout(() => setDisplayCartUpdateAwareness(false), 1000);
  };

  const saveCart = async () => {
    if (cart) {
      const savedCart = await saveCartMutation.mutateAsync(cart);
      setCart(savedCart);
      setIsCartChanged(false);

      const oldCartAmountAwayFromFreeShipping = parseFloat(
        persistedCart?.priceSummary?.amountAwayFromFreeShipping?.replace('$', '') ?? '0',
      );
      const newCartAmountAwayFromFreeShipping = parseFloat(
        cart?.priceSummary?.amountAwayFromFreeShipping?.replace('$', '') ?? '0',
      );
      // if this save unlocks free shipping, show notification
      if (oldCartAmountAwayFromFreeShipping > 0 && newCartAmountAwayFromFreeShipping <= 0) {
        setDisplayFreeShippingUnlockedNotification(true);
      }
    }
  };

  const recalculateCart = async (pendingCart: Cart) => {
    setIsCartLoading(true);
    const recalculatedCart = await updatePendingCartMutation.mutateAsync(pendingCart);
    setCart(recalculatedCart);
    setIsCartLoading(false);
    return recalculatedCart;
  };

  const canIncreaseQuantity = (menuItem: MenuCartItem):
    boolean => (menuItem as any).isInStock !== false;

  const addToCart = async (menuItem: MenuCartItem, isPromoCartItem =
  false, suppressDrawer = false) => {
    if (!canIncreaseQuantity(menuItem)) return;
    if (cart) {
      const updatedCart: Cart = {
        ...cart,
        cartItems: [
          ...cart?.cartItems || [],
          {
            ...menuItem,
            quantity: isPromoCartItem ? 0 : 1,
            isPromoCartItem,
          },
        ],
      };
      await recalculateCart(updatedCart);
      handleCartUpdated();

      if (isLoggedIn) {
        setIsCartChanged(true);
      } else if (!suppressDrawer) {
        setIsCartDrawerOpen(true);
      }
    }
  };

  const removeFromCart = async (deletedItem: MenuCartItem) => {
    if (!cart) {
      return;
    }

    setIsCartDrawerOpen(true); // ensure cart drawer is open when an item is removed
    setIsCartLoading(true); // the recalculateCart function will handle resetting this to false

    // for REMOVED_CART_ITEM_TIMEOUT ms, set cart item to quantity = 0 so removedCartItem displays
    const removedCartItemCart: Cart = {
      ...cart,
      cartItems: cart.cartItems.map((cartItem) => (cartItem.skuId === deletedItem.skuId
        ? {
          ...cartItem,
          quantity: 0,
        }
        : cartItem)),
    };
    setCart(removedCartItemCart);

    if (isLoggedIn) setIsCartChanged(true);
    // use timeoutKey to handle multiple removes at same time
    const timeoutKey = deletedItem.skuId;

    // clear timeout if existing is already running
    const existing = timeoutMapRef.current.get(timeoutKey);
    if (existing) clearTimeout(existing);

    // after removedCartItem timeout seconds, filter out removedCartItem ONLY IF quantity is still 0
    // if quantity is not 0, the user clicked "undo" so do not filter out
    const timeoutId = setTimeout(async () => {
      setCart((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          cartItems: prev.cartItems.filter(
            (item) => item.skuId !== deletedItem.skuId
              || Number(item.quantity) > 0,
          ),
        };
      });
      timeoutMapRef.current.delete(timeoutKey);

      // recalc with latest cart
      if (timeoutMapRef.current.size === 0) {
        setCart((currentCart) => {
          if (currentCart) {
            recalculateCart(currentCart);
            return currentCart;
          }
          return cart;
        });
      }
    }, REMOVED_CART_ITEM_TIMEOUT);
    // store in map for case where user removes, undo's, and removes again within timeout interval
    timeoutMapRef.current.set(timeoutKey, timeoutId);
  };

  const updateCartQuantity = async (updatedItem: MenuCartItem, quantity: number) => {
    const currentQuantity = updatedItem.quantity || 0;
    const isIncreasing = quantity > currentQuantity;
    if (isIncreasing && !canIncreaseQuantity(updatedItem)) {
      return;
    }
    if (quantity === 0) {
      await removeFromCart(updatedItem);
      return;
    }

    if (cart) {
      const oldCartAmountAwayFromFreeShipping = parseFloat(cart.priceSummary
        ?.amountAwayFromFreeShipping.replace('$', ''));
      const updatedCart: Cart = { ...cart };
      const selectedItem = updatedCart.cartItems
        .find((cartItem) => cartItem.skuId === updatedItem.skuId);

      if (selectedItem) {
        selectedItem.quantity = quantity;
      }

      const newCart = await recalculateCart(updatedCart);
      if (isLoggedIn) setIsCartChanged(true);

      const newCartAmountAwayFromFreeShipping = parseFloat(
        newCart?.priceSummary?.amountAwayFromFreeShipping?.replace('$', '') ?? '0',
      );

      // Show free shipping banner if old cart did not have free shipping and updated newCart does
      setDisplayFreeShippingUnlockedBanner(oldCartAmountAwayFromFreeShipping > 0
        && newCartAmountAwayFromFreeShipping <= 0);
    }
  };

  const discardCartChanges = () => {
    setCart(persistedCart);
    setIsCartChanged(false);
  };

  const onSelectMenu = (selectedMenuId: number, selectedCartId: number) => {
    setActiveMenuId(selectedMenuId);
    setActiveCartId(selectedCartId);
  };

  const cartIds = upcomingMenus?.menus.filter((m: Menu) => m?.cartId !== null)
    .map((m: Menu) => m?.cartId) || [];
  const reactivateSubscriptionMutation = useReactivateSubscriptionMutation(cartIds);
  const reactivateSubscription = async () => {
    await reactivateSubscriptionMutation.mutateAsync();
    if (cart) {
      // When the subscription switches from inactive to active,
      // the previous cart summary which had a 0 total due to cart not being planned
      // needs to be replaced with a price recalculation.
      await recalculateCart(cart);
    }
  };

  const addFavorite = async (skuId: number) => {
    const response: IApiResponse | undefined = await favoriteProduct.mutateAsync(skuId);
    if (!response) {
      console.error('Failed to addFavorite on product');
    }
  };

  const removeFavorite = async (skuId: number) => {
    const response: IApiResponse | undefined = await unFavoriteProduct.mutateAsync(skuId);
    if (!response) {
      console.error('Failed to removeFavorite on product');
    }
  };

  const toggleSkip = async () => {
    if (cart) {
      const updatedCart = {
        ...cart,
        isSkipped: !cart.isSkipped,
      };
      await recalculateCart(updatedCart);
      await saveCartMutation.mutateAsync(updatedCart);
      setIsCartChanged(false);
    }
  };

  const contextValue: ContextValue = {
    upcomingMenus,
    menuWithCartItems,
    cart,
    menu,
    isMenuLoading: isPersistedMenuLoading,
    isCartLoading: isPersistedCartLoading || isCartLoading,
    menuError: persistedMenuError,
    cartError: persistedCartError,
    saveCart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    canIncreaseQuantity,
    discardCartChanges,
    onSelectMenu,
    isCartDrawerOpen,
    setIsCartDrawerOpen,
    isCartChanged,
    setIsCartChanged,
    displayFreeShippingUnlockedBanner,
    setDisplayFreeShippingUnlockedBanner,
    displayFreeShippingUnlockedNotification,
    setDisplayFreeShippingUnlockedNotification,
    currentSignupStep,
    setCurrentSignupStep,
    displayChangeCartAddress,
    setDisplayChangeCartAddress,
    cartShippingAddressId,
    setCartShippingAddressId,
    displayCartUpdateAwareness,
    recalculateCart,
    selectedFilters,
    setSelectedFilters,
    selectedDropdownFilters,
    setSelectedDropdownFilters,
    filteredResultCount,
    setFilteredResultCount,
    reactivateSubscription,
    addFavorite,
    removeFavorite,
    toggleSkip,
    numFavoriteItems,
    setNumFavoriteItems,
  };

  return (
    <MenuWithCartContext.Provider value={contextValue}>
      {children}
    </MenuWithCartContext.Provider>
  );
}

export function useMenuWithCart(): ContextValue {
  const context = useContext(MenuWithCartContext);
  if (!context) throw new Error('useMenuWithCart must be used within MenuWithCartProvider');
  return context;
}
